/* Version: 3.5.149 */
'use client';
import 'react';
import PropTypes from 'prop-types';
import Tooltip from '../Tooltip/index.js';
import i18n from '../../lang/i18n/index.js';
import { sanitizeSpaces } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { PROGRESS_BAR_VARIANT } from './constants.js';
import { Root, Label, SolidBarWrapper, StepBarWrapper, StepWrapper, Step } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useLang from '../../hooks/useLang.js';
import useTheme from '../../hooks/useTheme.js';
import { TOOLTIP_SIDES } from '../Tooltip/constants.js';
import numericRange from '../../prop-types/numericRange.js';

var ProgressBar = function ProgressBar(_ref) {
  var _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? PROGRESS_BAR_VARIANT.PERCENTAGE : _ref$variant,
    _ref$progressPerc = _ref.progressPerc,
    progressPerc = _ref$progressPerc === undefined ? 0 : _ref$progressPerc,
    _ref$currentStep = _ref.currentStep,
    currentStep = _ref$currentStep === undefined ? 0 : _ref$currentStep,
    _ref$steps = _ref.steps,
    steps = _ref$steps === undefined ? [] : _ref$steps,
    _ref$completionText = _ref.completionText,
    completionText = _ref$completionText === undefined ? '' : _ref$completionText,
    _ref$stepWidth = _ref.stepWidth,
    stepWidth = _ref$stepWidth === undefined ? 100 : _ref$stepWidth,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {},
      labelStyle: {},
      progressBarStyle: {},
      stepStyle: {}
    } : _ref$customStyle;
  var lang = useLang();
  var theme = useTheme();
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle,
    labelStyle = _safeCustomStyle.labelStyle,
    progressBarStyle = _safeCustomStyle.progressBarStyle,
    stepStyle = _safeCustomStyle.stepStyle;
  var progressPercent = Math.round(progressPerc * 100);
  var totalSteps = steps.length;
  if (variant === PROGRESS_BAR_VARIANT.PERCENTAGE) {
    return /*#__PURE__*/jsxs(Root, {
      rootStyle: rootStyle,
      children: [/*#__PURE__*/jsx(Label, {
        labelStyle: labelStyle,
        theme: theme,
        children: "".concat(progressPercent, "% ").concat(completionText ? completionText : '')
      }), /*#__PURE__*/jsx(SolidBarWrapper, {
        progressBarStyle: progressBarStyle,
        perc: progressPercent,
        theme: theme
      })]
    });
  }
  if (variant === PROGRESS_BAR_VARIANT.STEPS) {
    return /*#__PURE__*/jsxs(Root, {
      rootStyle: rootStyle,
      children: [/*#__PURE__*/jsx(Label, {
        labelStyle: labelStyle,
        width: totalSteps * stepWidth,
        theme: theme,
        children: i18n[lang].progressBar.labelTemplate({
          currentStep: currentStep,
          totalSteps: totalSteps
        })
      }), /*#__PURE__*/jsx(StepBarWrapper, {
        progressBarStyle: progressBarStyle,
        width: totalSteps * stepWidth,
        theme: theme,
        children: steps.map(function (e, i) {
          var isDone = i < currentStep;
          var isCurrent = i === currentStep - 1;
          var ariaLabelStatus = isCurrent ? "".concat(i18n[lang].progressBar.ariaLabelCurrent) : "".concat(i18n[lang].progressBar.ariaLabelComplete);
          var title = e.title,
            _e$color = e.color,
            color = _e$color === undefined ? theme.colorPalette.primaryColors.main.shade_default : _e$color;
          var ariaLabel = "".concat(title, ", ").concat(ariaLabelStatus);
          return /*#__PURE__*/jsx(StepWrapper, {
            "data-testid": e.title,
            children: isDone ? /*#__PURE__*/jsx(Tooltip, {
              disableAriaForPopup: true,
              ariaLabel: ariaLabel,
              id: "".concat(sanitizeSpaces(e.title), "-tooltip"),
              title: title,
              side: TOOLTIP_SIDES.TOP,
              bubbleWidth: stepWidth,
              customStyle: {
                rootStyle: {
                  width: '100%'
                }
              },
              children: /*#__PURE__*/jsx(Step, {
                stepStyle: stepStyle,
                color: color
              })
            }) : /*#__PURE__*/jsx(Step, {
              stepStyle: stepStyle,
              color: theme.colorPalette.neutralColors.light.shade_default
            })
          }, e.title);
        })
      })]
    });
  }
  return null;
};
process.env.NODE_ENV !== "production" ? ProgressBar.propTypes = {
  /**
   * Text displayed beside the progress percentage.
   *
   * This prop only works with the PERCENTAGE variant.
   * */
  completionText: PropTypes.string,
  /** Type of progress bar */
  variant: PropTypes.oneOf([PROGRESS_BAR_VARIANT.PERCENTAGE, PROGRESS_BAR_VARIANT.STEPS]),
  /**
   * The current step that we are on.
   *
   * This prop only works with the STEPS variant.
   */
  currentStep: PropTypes.number,
  /** Width of the individual step in pixels */
  stepWidth: PropTypes.number,
  /**
   * Meta information for each step like color, title (i.e. hover text)
   *
   * This prop only works with the STEPS variant.
   */
  steps: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    color: PropTypes.string
  })),
  /**
   * Progress as a real number, represents percentage complete.
   *
   * This prop only works with the PERCENTAGE variant.
   * */
  progressPerc: function progressPerc(props, propName, componentName) {
    var variant = props.variant;
    if (variant !== PROGRESS_BAR_VARIANT.PERCENTAGE) {
      return null;
    }
    return numericRange(0, 1)(props, propName, componentName);
  },
  /** Style overrides */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object,
    labelStyle: PropTypes.object,
    progressBarStyle: PropTypes.object,
    stepStyle: PropTypes.object
  })
} : undefined;
process.env.NODE_ENV !== "production" ? ProgressBar.displayName = 'ProgressBar' : undefined;

export { ProgressBar as default };
